import { Stack, Typography, useTheme } from "@mui/material";
import { MapPin } from "lucide-react";
import type { FC } from "react";
import { ActivityInfoRow } from "web_myplann/Activities/Components/ActivityInfoRow";
import { useFormatDateTimeByClientActivity } from "web_myplann/Activities/Hooks/useActivitiesFormatDateAndTimes";
import type { ActivityDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { useFormatUserWithType } from "web_myplann/Shared/Hooks/useFormatUserWithType";
import { getMapsLink } from "web_myplann/Utils/getMapsLink";

const R = Resources.Activity.Detail;

type Props = {
	event: ActivityDetailResponseDTO;
	hasMoreAgents: boolean;
};

export const ActivityBasicInfoSection: FC<Props> = ({
	event,
	hasMoreAgents,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatUserWithType = useFormatUserWithType();
	const formatDateTime = useFormatDateTimeByClientActivity();

	const activityType = event.type
		? t(Resources.Activity.List.ActivityType[event.type])
		: "";
	const mapsUrl = getMapsLink(event.place ?? "");

	return (
		<>
			<Typography variant="sectionLabel">{t(R.BasicInfo.title)}</Typography>
			<Stack bgcolor="background.paper" px={1.6} py={1.2} gap={1.2}>
				{event.title && (
					<ActivityInfoRow label={t(R.BasicInfo.Name)} value={event.title} />
				)}
				<ActivityInfoRow label={t(R.BasicInfo.Type)} value={activityType} />
				<ActivityInfoRow
					label={t(R.BasicInfo.Date)}
					value={formatDateTime(event)}
				/>
				{event.place && (
					<ActivityInfoRow label={t(R.BasicInfo.Address)}>
						<Stack
							component="a"
							href={mapsUrl}
							direction="row"
							alignItems="flex-start"
							gap={theme.spacingTokens.xs}
							sx={{ textDecoration: "none" }}
						>
							<MapPin
								size={theme.iconSize.xs}
								color={theme.palette.blue[600]}
								style={{ marginTop: 3, flexShrink: 0 }}
							/>
							<Typography variant="bodyMedium" color={theme.palette.blue[600]}>
								{event.place}
							</Typography>
						</Stack>
					</ActivityInfoRow>
				)}
				{hasMoreAgents && (
					<ActivityInfoRow
						label={t(R.BasicInfo.CreatedBy)}
						value={formatUserWithType(event.author)}
					/>
				)}
			</Stack>
		</>
	);
};
